import * as React from 'react';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { cn } from '../../lib/cn';

/**
 * Tabs — shadcn idiom on `@radix-ui/react-tabs`, themed onto the HofSync brand
 * tokens. Unlike Select/Tooltip/Dialog/Popover this is an INLINE primitive (no
 * portal): everything renders inside the DS subtree.
 *
 * Preflight is excluded from the DS Tailwind build, so each `TabsTrigger` — a
 * native `<button>` via Radix — keeps its UA chrome unless reset
 * per-component: `tw:appearance-none tw:border-0 tw:bg-transparent` + explicit
 * focus-ring + `tw:font-sans` (form controls don't inherit font-family, #1927
 * — carried here from day one). The active trigger sits on the `--card`
 * surface inside a `--muted` list, so both themes inherit for free via the
 * semantic tokens.
 *
 * `@radix-ui/react-tabs` is a real `dependency` (the DS ships as source); the
 * names are collision-free in the public-API barrel.
 */
const Tabs = TabsPrimitive.Root;

export type TabsProps = React.ComponentPropsWithoutRef<
  typeof TabsPrimitive.Root
>;
export type TabsListProps = React.ComponentPropsWithoutRef<
  typeof TabsPrimitive.List
>;
export type TabsTriggerProps = React.ComponentPropsWithoutRef<
  typeof TabsPrimitive.Trigger
>;
export type TabsContentProps = React.ComponentPropsWithoutRef<
  typeof TabsPrimitive.Content
>;

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  TabsListProps
>(({ className, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(
      'tw:inline-flex tw:h-9 tw:items-center tw:justify-center tw:gap-1 tw:rounded-lg tw:bg-muted tw:p-1 tw:text-muted-foreground',
      className
    )}
    {...props}
  />
));
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  TabsTriggerProps
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    // Native <button>: full UA reset (appearance/border/background/FONT, #1927)
    // — preflight is excluded, nothing resets it globally.
    className={cn(
      'tw:inline-flex tw:appearance-none tw:items-center tw:justify-center tw:whitespace-nowrap tw:rounded-md tw:border-0 tw:bg-transparent tw:px-3 tw:py-1 tw:font-sans tw:text-sm tw:font-medium tw:text-muted-foreground tw:transition-colors',
      'tw:focus-visible:outline-none tw:focus-visible:ring-2 tw:focus-visible:ring-ring',
      'tw:disabled:pointer-events-none tw:disabled:opacity-50',
      'tw:data-[state=active]:bg-card tw:data-[state=active]:text-foreground tw:data-[state=active]:shadow-sm',
      className
    )}
    {...props}
  />
));
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  TabsContentProps
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      'tw:mt-2 tw:text-foreground tw:focus-visible:outline-none tw:focus-visible:ring-2 tw:focus-visible:ring-ring',
      className
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsList, TabsTrigger, TabsContent };
